import React, { useState, useEffect } from 'react';
import {
  MapPin, CheckCircle2, Menu, X, ChevronDown, ArrowRight, MessageSquare,
  LayoutGrid, GraduationCap, Clock, ShieldCheck, Sparkles, ClipboardList
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { siteData } from '../data/siteData';
import ApplicationModal from '../components/ApplicationModal';

const C = { accent: '#e685a5', accentLight: '#f0a8c0', bg: '#0b0b0f', card: '#121218', muted: '#1a1a22' };
const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const iconMap = { LayoutGrid, GraduationCap, Clock, ShieldCheck };

const NAV = [
  { label: 'Guide', id: 'guide' },
  { label: 'Environment', id: 'environment' },
  { label: 'FAQ', id: 'faq' },
  { label: 'Enquire', id: 'enquire' },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const JobsHome = () => {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showFloat, setShowFloat] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [applyOpen, setApplyOpen] = useState(false);

  const d = siteData;
  const openApply = () => setApplyOpen(true);

  useEffect(() => {
    const h = () => setShowFloat(window.scrollY > 600);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const proseClasses = "text-gray-600 text-[15px] leading-[1.85] [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul_li]:mb-2 [&_strong]:text-gray-900";

  return (
    <div className="min-h-screen" style={{ background: C.bg, fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="jobs-home">

      {/* ── Header ── */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b" style={{ background: `${C.bg}ee`, borderColor: `${C.accent}15` }} data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[72px]">
          <button onClick={() => scrollTo('top')} className="flex items-center gap-2.5 text-left">
            <span className="w-9 h-9 rounded-lg flex items-center justify-center text-base font-bold flex-shrink-0" style={{ ...serif, background: `${C.accent}1f`, color: C.accent, border: `1px solid ${C.accent}40` }}>AE</span>
            <div className="hidden sm:block">
              <h1 className="text-sm font-semibold text-white leading-tight tracking-wide">Adult Entertainment Jobs</h1>
              <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: `${C.accent}cc` }}>Melbourne</p>
            </div>
          </button>
          <nav className="hidden md:flex items-center gap-9">
            {NAV.slice(0, 4).map(i => (
              <button key={i.id} onClick={() => scrollTo(i.id)} className="text-[12px] text-gray-400 hover:text-white transition-colors tracking-[0.15em] uppercase font-medium">{i.label}</button>
            ))}
            <Button className="text-[12px] font-semibold px-6 h-10 tracking-wide text-white hover:opacity-90" style={{ background: C.accent }} onClick={openApply} data-testid="header-cta">
              Apply Now
            </Button>
          </nav>
          <button className="md:hidden text-white p-2" onClick={() => setMobileMenu(!mobileMenu)} aria-label="Menu">
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden px-4 py-4 space-y-2" style={{ background: C.card, borderTop: `1px solid ${C.accent}15` }}>
            {NAV.map(i => (
              <button key={i.id} className="block w-full text-left text-gray-300 py-2 text-sm tracking-wide uppercase" onClick={() => { scrollTo(i.id); setMobileMenu(false); }}>{i.label}</button>
            ))}
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section id="top" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="/media/image63.jpeg" alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${C.bg}dd 0%, ${C.bg}bb 45%, ${C.bg} 100%)` }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20 text-center">
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full" style={{ background: `${C.accent}15`, border: `1px solid ${C.accent}30` }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: C.accent }} />
            <span className="text-xs font-semibold tracking-[0.15em] uppercase" style={{ color: C.accent }}>{d.hero.badge}</span>
          </div>
          <h2 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-4 leading-[1.06]" style={serif}>
            {d.hero.titleLead}<br /><span style={{ color: C.accent }}>{d.hero.titleAccent}</span>
          </h2>
          <p className="text-xl sm:text-2xl text-gray-200 mb-5" style={serif}>{d.hero.titleTail}</p>
          <p className="text-lg text-gray-400 mb-10 leading-relaxed max-w-2xl mx-auto">{d.hero.subtitle}</p>
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            {d.hero.stats.map(([n, l]) => (
              <div key={l} className="px-5 py-3 rounded-xl backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="text-lg font-bold text-white">{n}</div>
                <div className="text-[10px] text-gray-400 uppercase tracking-[0.15em]">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" className="text-white font-bold h-14 px-10 text-base rounded-full hover:scale-105 transition-transform duration-300" style={{ background: `linear-gradient(135deg, ${C.accent}, #d4648a)`, boxShadow: `0 12px 40px ${C.accent}40` }} onClick={openApply} data-testid="hero-cta-button">
              Apply Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ── Intro ── */}
      <section className="py-24" style={{ background: C.bg }} data-testid="intro-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" style={serif}>Start Your Journey Here</h2>
            <div className="w-16 h-0.5 mx-auto" style={{ background: C.accent }} />
          </div>
          <div className="space-y-5">
            {d.intro.paragraphs.map((p, i) => (
              <p key={i} className="text-gray-400 text-[15px] leading-[1.9]">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="pb-24" style={{ background: C.bg }} data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {d.features.map((f, i) => {
              const Ic = iconMap[f.icon] || Sparkles;
              return (
                <div key={i} className="group p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ background: `${C.accent}15`, color: C.accent }}>
                    <Ic className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Guide (Part 1) ── */}
      <section id="guide" className="py-24 bg-white" data-testid="guide-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={serif}>{d.seoContent.title}</h2>
            <div className="w-16 h-0.5 mx-auto" style={{ background: C.accent }} />
          </div>
          <article>
            {d.seoContent.sections.slice(0, 4).map((s, i) => (
              <div key={i} className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" style={serif}>{s.heading}</h2>
                <div className={proseClasses} dangerouslySetInnerHTML={{ __html: s.content }} />
                <div className="w-12 h-px bg-gray-200 mt-10" />
              </div>
            ))}
          </article>
        </div>
      </section>

      {/* ── Environment / Gallery ── */}
      <section id="environment" className="py-24" style={{ background: C.bg }} data-testid="environment-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={serif}>A Professional Working Environment</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">The venue, facilities and people you work with matter. Look for private changing areas, staff facilities, support and security.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.gallery.map((g, i) => (
              <div key={i} className="group relative rounded-2xl overflow-hidden aspect-[4/3]" data-testid={`gallery-item-${i}`}>
                <img src={g.photo} alt={g.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-5 text-white font-semibold text-base tracking-wide" style={serif}>{g.caption}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Questions checklist ── */}
      <section className="py-24 bg-[#f7f7f9]" data-testid="questions-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5" style={{ background: `${C.accent}15`, color: C.accent }}>
              <ClipboardList className="w-7 h-7" />
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={serif}>Questions to Ask Before You Accept a Job</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">A job advert can only tell you so much. The conversation with the employer is where you get the information that helps you make the best decision.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.questions.map((q, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-5 py-4 shadow-sm">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: C.accent }} />
                <span className="text-gray-800 text-sm font-medium">{q}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-10 max-w-2xl mx-auto">Asking for clarity on any role you go for can give you all the details you need to make the right decision before you commit.</p>
        </div>
      </section>

      {/* ── Guide (Part 2) ── */}
      <section className="py-24 bg-white" data-testid="guide-section-2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article>
            {d.seoContent.sections.slice(4).map((s, i, arr) => (
              <div key={i} className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" style={serif}>{s.heading}</h2>
                <div className={proseClasses} dangerouslySetInnerHTML={{ __html: s.content }} />
                {i < arr.length - 1 && <div className="w-12 h-px bg-gray-200 mt-10" />}
              </div>
            ))}
          </article>
        </div>
      </section>

      {/* ── CTA + Enquiry ── */}
      <section id="enquire" className="py-24" style={{ background: `linear-gradient(180deg, ${C.bg} 0%, ${C.card} 100%)` }} data-testid="enquire-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5" style={serif}>{d.cta.heading}</h2>
              <div className="w-16 h-0.5 mb-6" style={{ background: C.accent }} />
              {d.cta.paragraphs.map((p, i) => (
                <p key={i} className="text-gray-400 text-[15px] leading-[1.9] mb-4">{p}</p>
              ))}
              <div className="mt-8 space-y-3">
                <a href={d.business.smsHref} className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors text-sm">
                  <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${C.accent}15`, color: C.accent }}><MessageSquare className="w-5 h-5" /></span>
                  <span>SMS {d.business.contactName}: <span className="font-semibold text-white">{d.business.sms}</span></span>
                </a>
                <div className="flex items-center gap-3 text-gray-300 text-sm">
                  <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${C.accent}15`, color: C.accent }}><MapPin className="w-5 h-5" /></span>
                  {d.business.location}
                </div>
                <p className="text-xs text-gray-500 pt-1">{d.business.note}</p>
              </div>
            </div>

            {/* Apply card */}
            <div className="rounded-2xl p-8 text-center flex flex-col items-center justify-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.accent}20` }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ background: `${C.accent}18`, color: C.accent }}>
                <ClipboardList className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" style={serif}>Confidential Application</h3>
              <p className="text-gray-400 text-sm mb-6 max-w-xs">Complete a quick, confidential application and we&rsquo;ll be in touch about roles that suit you. Confidentiality assured.</p>
              <Button className="w-full text-white font-bold h-13 py-3 tracking-wide uppercase" style={{ background: C.accent }} onClick={openApply} data-testid="open-apply-cta">
                Apply for Jobs Now
              </Button>
              <p className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-4">
                <MessageSquare className="w-3.5 h-3.5" style={{ color: C.accent }} />
                Questions? SMS {d.business.contactName}: {d.business.sms} (no calls)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={serif}>Frequently Asked Questions</h2>
            <p className="text-gray-500">Everything you need to know before you start</p>
          </div>
          <div className="space-y-3">
            {d.faqs.map((faq, i) => (
              <div key={i} className="rounded-xl border border-gray-200 overflow-hidden transition-all" style={{ background: openFaq === i ? `${C.accent}08` : 'white' }}>
                <button className="w-full flex items-center justify-between p-5 text-left" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`faq-${i}`}>
                  <h3 className="font-semibold text-gray-900 text-[15px] pr-4">{faq.question}</h3>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} style={{ color: C.accent }} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="px-5 pb-5 text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t pt-16 pb-8 text-white" style={{ background: C.bg, borderColor: `${C.accent}12` }} data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-9 h-9 rounded-lg flex items-center justify-center text-base font-bold" style={{ ...serif, background: `${C.accent}1f`, color: C.accent, border: `1px solid ${C.accent}40` }}>AE</span>
                <span className="text-white font-semibold text-sm">Adult Entertainment Jobs Melbourne</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">Helping you explore adult entertainment jobs across Melbourne &mdash; openly, honestly and on your terms.</p>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold mb-4">Explore</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                {NAV.map(i => <li key={i.id}><button onClick={() => scrollTo(i.id)} className="hover:text-white transition-colors">{i.label}</button></li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold mb-4">Roles</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                {d.roles.map(s => <li key={s.title}>{s.title}</li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href={d.business.smsHref} className="hover:text-white transition-colors">SMS {d.business.contactName}: {d.business.sms}</a></li>
                <li className="text-gray-600">{d.business.note}</li>
                <li>{d.business.location}</li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8 text-center text-xs text-gray-600 space-y-1" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            <p>&copy; {new Date().getFullYear()} Adult Entertainment Jobs Melbourne. All rights reserved.</p>
            <p>Adults Only (18+) &middot; Information for job seekers in the adult entertainment sector</p>
          </div>
        </div>
      </footer>

      {/* Floating Apply button */}
      <div className={`fixed bottom-6 left-6 z-50 transition-all duration-500 ${showFloat ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}>
        <Button className="btn-shine animate-flicker text-white font-bold px-6 h-12 rounded-xl text-sm hover:scale-105 transition-transform" style={{ background: 'linear-gradient(135deg, #f0247f 0%, #d1156a 100%)' }} onClick={openApply} data-testid="floating-cta">
          <Sparkles className="w-4 h-4 mr-2" /> Apply For Adult Jobs Now
        </Button>
      </div>

      <ApplicationModal open={applyOpen} onOpenChange={setApplyOpen} />
    </div>
  );
};

export default JobsHome;
